import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";

const CURRENCY_SYMBOLS = { USD: "$", GBP: "£", EUR: "€", CAD: "$", AUD: "$" };

function formatMoney(amount, currency = "USD") {
  const sym = CURRENCY_SYMBOLS[currency] || "";
  const n = Number(amount || 0).toFixed(2);
  return sym ? `${sym}${n}` : `${n} ${currency}`;
}

export default function CartDrawer() {
  const { cart, open, closeDrawer, updateLine, removeLine, loading } = useCart();

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeDrawer]);

  const lines = cart?.lines || [];
  const currency = cart?.currency || "USD";
  const isEmpty = lines.length === 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[80] bg-[#0F0F0F]/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeDrawer}
        aria-hidden={!open}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Shopping cart"
        aria-hidden={!open}
        className={`fixed bottom-0 right-0 top-0 z-[90] flex w-full max-w-md flex-col border-l-2 border-[#1A3626] bg-[#F9F9F4] shadow-[-12px_0_0_0_#1A3626] transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <header className="flex items-center justify-between border-b-2 border-[#1A3626] bg-[#1A3626] px-5 py-4 text-[#F9F9F4]">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" strokeWidth={2.6} />
            <span className="font-display text-lg font-black uppercase tracking-widest">
              Your Cart
            </span>
            {cart?.total_quantity ? (
              <span
                className="font-display ml-1 inline-flex items-center justify-center rounded-full bg-[#93C065] px-2 py-0.5 text-xs font-black text-[#0F0F0F]"
                data-testid="cart-drawer-count"
              >
                {cart.total_quantity}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-9 w-9 items-center justify-center border-2 border-[#F9F9F4] transition-colors hover:bg-[#93C065] hover:text-[#0F0F0F]"
            aria-label="Close cart"
            data-testid="cart-close"
          >
            <X className="h-5 w-5" strokeWidth={2.6} />
          </button>
        </header>

        {/* Scrollable item list */}
        <div className="flex-1 overflow-y-auto" data-testid="cart-lines">
          {isEmpty ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 px-6 py-20 text-center">
              <div className="font-display text-2xl font-black uppercase tracking-tight">
                Cart is dry.
              </div>
              <p className="max-w-xs text-sm text-[#1A3626]">
                Nothing in here yet. Go pick up a deeply confused tee.
              </p>
              <button
                type="button"
                onClick={closeDrawer}
                className="wc-btn mt-2"
                data-testid="cart-empty-shop"
              >
                Continue shopping
              </button>
            </div>
          ) : (
            <ul className="divide-y-2 divide-[#1A3626]/15">
              {lines.map((line) => (
                <li
                  key={line.line_id}
                  className="flex gap-4 px-5 py-5"
                  data-testid={`cart-line-${line.line_id}`}
                >
                  <Link
                    to={`/shop/${line.product_handle}`}
                    onClick={closeDrawer}
                    className="block h-24 w-20 shrink-0 overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD]"
                  >
                    {line.image ? (
                      <img
                        src={line.image}
                        alt={line.product_title}
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </Link>

                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/shop/${line.product_handle}`}
                        onClick={closeDrawer}
                        className="font-display line-clamp-2 text-sm font-black leading-tight hover:underline"
                      >
                        {line.product_title}
                      </Link>
                      <button
                        type="button"
                        onClick={() => removeLine(line.line_id)}
                        disabled={loading}
                        className="text-[#1A3626]/60 transition-colors hover:text-[#0F0F0F] disabled:opacity-40"
                        aria-label="Remove item"
                        data-testid={`cart-remove-${line.line_id}`}
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={2.4} />
                      </button>
                    </div>

                    {line.variant_title && line.variant_title.toLowerCase() !== "default title" ? (
                      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#1A3626]/70">
                        {line.variant_title}
                      </div>
                    ) : null}

                    <div className="mt-3 flex items-center justify-between">
                      <div
                        className="flex items-stretch border-2 border-[#1A3626]"
                        data-testid={`cart-qty-${line.line_id}`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            updateLine(line.line_id, Math.max(0, line.quantity - 1))
                          }
                          disabled={loading}
                          className="px-2 font-display font-black disabled:opacity-40"
                          aria-label="decrease quantity"
                        >
                          <Minus className="h-3 w-3" strokeWidth={3} />
                        </button>
                        <div className="min-w-[36px] border-x-2 border-[#1A3626] px-2 py-1 text-center font-display text-sm font-bold">
                          {line.quantity}
                        </div>
                        <button
                          type="button"
                          onClick={() => updateLine(line.line_id, line.quantity + 1)}
                          disabled={loading}
                          className="px-2 font-display font-black disabled:opacity-40"
                          aria-label="increase quantity"
                        >
                          <Plus className="h-3 w-3" strokeWidth={3} />
                        </button>
                      </div>
                      <div className="font-display text-sm font-black">
                        {formatMoney(line.price * line.quantity, line.currency || currency)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!isEmpty && (
          <footer className="border-t-2 border-[#1A3626] bg-[#EBE7DD] px-5 py-5">
            <div className="flex items-baseline justify-between">
              <span className="font-display text-xs font-bold uppercase tracking-[0.3em]">
                Subtotal
              </span>
              <span className="font-display text-2xl font-black" data-testid="cart-subtotal">
                {formatMoney(cart?.subtotal ?? cart?.total ?? 0, currency)}
              </span>
            </div>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#1A3626]/70">
              Shipping &amp; taxes calculated at checkout.
            </p>
            <a
              href={cart?.checkout_url || "#"}
              className="wc-btn mt-4 flex w-full items-center justify-center"
              data-testid="cart-checkout"
            >
              Checkout <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.8} />
            </a>
            <button
              type="button"
              onClick={closeDrawer}
              className="font-display mt-3 w-full py-2 text-center text-xs font-bold uppercase tracking-[0.3em] underline-offset-4 hover:underline"
              data-testid="cart-continue"
            >
              Continue shopping
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
